// Утилита Markdown → HTML с санитайзингом.
//
// Зачем выносить отдельно:
//   1) marked.setOptions() — глобальная настройка пакета. Если её делать
//      внутри компонента, то она перепишется на каждый рендер каждого
//      места, использующего marked — это и не нужно, и опасно.
//   2) DOMPurify должен иметь явный whitelist тегов и атрибутов. По умолчанию
//      он разрешает большой список, который для контента FAQ/статей избыточен.
//   3) Если когда-нибудь захотим заменить marked на другой парсер (например,
//      markdown-it) — поменять только тут.
//
// Использование:
//   import { renderMarkdown } from '../../utils/markdown';
//   <div dangerouslySetInnerHTML={{ __html: renderMarkdown(article.content) }} />

import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Настраиваем marked ОДИН раз при загрузке модуля.
// `breaks: true` — переносы строк → <br/> (привычно для русскоязычных авторов).
// `gfm: true` — GitHub Flavored Markdown (таблицы, чек-листы, ~~strikethrough~~).
// Опция `sanitize` в marked v16+ удалена (раньше была deprecated) — поэтому
// санитайзим строго через DOMPurify.
marked.setOptions({
    breaks: true,
    gfm: true,
});

// Whitelist для DOMPurify: только теги и атрибуты, которые могут появиться
// в контенте админки. Это защищает от XSS даже если пользователь введёт
// что-то странное в редакторе.
//
// Намеренно НЕ разрешаем:
//   - <script>, <iframe>, <object>, <embed> — очевидно опасны;
//   - on* атрибуты (onclick, onerror) — DOMPurify режет их по умолчанию;
//   - стили (style="..."), чтобы автор не мог сломать вёрстку;
//   - data:-URL в src — потенциальный вектор для PNG-XSS.
const PURIFY_CONFIG = {
    ALLOWED_TAGS: [
        // Текстовые
        'p', 'br', 'hr', 'span', 'div',
        'strong', 'em', 'b', 'i', 'u', 's', 'del', 'ins', 'mark', 'sub', 'sup',
        // Заголовки
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        // Списки
        'ul', 'ol', 'li',
        // Цитаты, код
        'blockquote', 'code', 'pre',
        // Таблицы (GFM)
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        // Ссылки и медиа
        'a', 'img',
    ],
    ALLOWED_ATTR: ['href', 'title', 'alt', 'src', 'class', 'target', 'rel'],
    // Запрещаем data: и javascript: схемы в href/src. Разрешаем http(s) и mailto.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|ftp):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

/**
 * Превращает markdown-текст в безопасный HTML.
 * Если на вход null/undefined/'' — возвращает пустую строку.
 */
export function renderMarkdown(markdown) {
    if (!markdown) return '';
    const rawHtml = marked(markdown);
    return DOMPurify.sanitize(rawHtml, PURIFY_CONFIG);
}

// Альтернативное имя для совместимости с другими местами кодовой базы.
export default renderMarkdown;
